import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PackageX, Wrench } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { libraryFinesApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { DamageRecord, LostBookRecord } from '@/types/library';
import { formatDate, orDash } from '@/utils/format';
import { Badge, Money, humanise } from './shared';

type Tab = 'lost' | 'damaged';

/**
 * Books that did not come back the way they went out.
 *
 * <p>A recovered book does not silently cancel its charge. The copy comes back
 * into stock either way, but whether the family is let off is an explicit choice
 * with a reason — a family that has already paid needs a refund through finance,
 * and pretending the debt evaporated would leave that money unaccounted for.
 */
export function LostAndDamagedPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const lostTable = useTableQuery('reportedOn,desc');
  const damagedTable = useTableQuery('reportedOn,desc');

  const manages = can(PERMISSIONS.LIBRARY_INVENTORY_MANAGE);
  const waives = can(PERMISSIONS.LIBRARY_FINE_WAIVE);

  const [tab, setTab] = useState<Tab>('lost');

  const lost = useQuery({
    queryKey: queryKeys.libraryFines.lost(lostTable.params),
    queryFn: () => libraryFinesApi.lost(lostTable.params),
    enabled: tab === 'lost',
  });

  const damaged = useQuery({
    queryKey: queryKeys.libraryFines.damaged(damagedTable.params),
    queryFn: () => libraryFinesApi.damaged(damagedTable.params),
    enabled: tab === 'damaged',
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.libraryFines.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.libraryInventory.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.library.all });
  };

  const recover = useMutation({
    mutationFn: ({ id, waiveFine }: { id: string; waiveFine: boolean }) =>
      libraryFinesApi.recoverLost(id, waiveFine),
    onSuccess: () => {
      toast.success('Back in stock');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const writeOff = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      libraryFinesApi.writeOffLoss(id, reason),
    onSuccess: () => {
      toast.info('Written off');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setDamageStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'UNDER_REPAIR' | 'REPAIRED' | 'WRITTEN_OFF' }) =>
      libraryFinesApi.setDamageStatus(id, status),
    onSuccess: (record) => {
      toast.success(
        record.status === 'REPAIRED'
          ? 'Back in circulation, in fair condition — a mended book is not a new one'
          : `Now ${humanise(record.status)}`,
      );
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const lostColumns: Column<LostBookRecord>[] = [
    {
      key: 'book',
      header: 'Book',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.bookTitle}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.accessionNumber}</span>
        </span>
      ),
    },
    { key: 'member', header: 'Who had it', render: (row) => orDash(row.memberName) },
    { key: 'reportedOn', header: 'Reported', render: (row) => formatDate(row.reportedOn) },
    {
      key: 'cost',
      header: 'Charged',
      className: 'text-right',
      render: (row) => <Money value={row.replacementCost} />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge
          tone={
            row.status === 'RECOVERED' ? 'success' : row.status === 'REPORTED' ? 'danger' : 'neutral'
          }
        >
          {humanise(row.status)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        row.status !== 'REPORTED' ? null : (
          <span className="flex justify-end gap-1.5">
            {manages && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  const waiveFine = window.confirm(
                    'The copy comes back into stock either way.\n\nOK: also waive the charge.\nCancel: keep the charge — the family has been billed and may have paid.',
                  );
                  recover.mutate({ id: row.id, waiveFine });
                }}
              >
                It turned up
              </Button>
            )}
            {waives && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const reason = window.prompt('Why is the loss being written off?');
                  if (reason !== null && reason.trim() !== '') {
                    writeOff.mutate({ id: row.id, reason: reason.trim() });
                  }
                }}
              >
                Write off
              </Button>
            )}
          </span>
        ),
    },
  ];

  const damagedColumns: Column<DamageRecord>[] = [
    {
      key: 'book',
      header: 'Book',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.bookTitle}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.accessionNumber}</span>
        </span>
      ),
    },
    { key: 'member', header: 'Who had it', render: (row) => orDash(row.memberName) },
    {
      key: 'damage',
      header: 'Damage',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{humanise(row.damageType)}</span>
          <span className="block text-[11px] text-subtle">{humanise(row.severity)}</span>
        </span>
      ),
    },
    { key: 'reportedOn', header: 'Reported', render: (row) => formatDate(row.reportedOn) },
    {
      key: 'cost',
      header: 'Repair',
      className: 'text-right',
      render: (row) => <Money value={row.repairCost} />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge
          tone={
            row.status === 'REPAIRED'
              ? 'success'
              : row.status === 'UNDER_REPAIR'
                ? 'warning'
                : row.status === 'REPORTED'
                  ? 'danger'
                  : 'neutral'
          }
        >
          {humanise(row.status)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        !manages || row.status === 'REPAIRED' || row.status === 'WRITTEN_OFF' ? null : (
          <span className="flex justify-end gap-1.5">
            {row.status === 'REPORTED' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setDamageStatus.mutate({ id: row.id, status: 'UNDER_REPAIR' })}
              >
                Send for repair
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setDamageStatus.mutate({ id: row.id, status: 'REPAIRED' })}
            >
              Repaired
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDamageStatus.mutate({ id: row.id, status: 'WRITTEN_OFF' })}
            >
              Beyond repair
            </Button>
          </span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Lost and damaged"
        description="Books that did not come back the way they went out."
        icon={<PackageX className="h-5 w-5" aria-hidden="true" />}
      />

      <div className="mb-3 flex gap-2">
        <TabButton active={tab === 'lost'} onClick={() => setTab('lost')} icon={PackageX}>
          Lost
        </TabButton>
        <TabButton active={tab === 'damaged'} onClick={() => setTab('damaged')} icon={Wrench}>
          Damaged
        </TabButton>
      </div>

      <Card>
        {tab === 'lost' ? (
          <DataTable
            columns={lostColumns}
            rows={lost.data?.data}
            rowKey={(row) => row.id}
            loading={lost.isPending}
            error={lost.error}
            onRetry={() => void lost.refetch()}
            sort={lostTable.sort}
            onSort={lostTable.toggleSort}
            emptyTitle="Nothing lost"
            emptyDescription="No copy has been reported lost."
          />
        ) : (
          <DataTable
            columns={damagedColumns}
            rows={damaged.data?.data}
            rowKey={(row) => row.id}
            loading={damaged.isPending}
            error={damaged.error}
            onRetry={() => void damaged.refetch()}
            sort={damagedTable.sort}
            onSort={damagedTable.toggleSort}
            emptyTitle="Nothing damaged"
            emptyDescription="No copy has been reported damaged."
          />
        )}
      </Card>
    </>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-2 border px-3 py-2 text-sm font-semibold ${
        active ? 'border-accent bg-accent-soft text-accent-soft-fg' : 'border-line bg-bg text-muted'
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {children}
    </button>
  );
}
